import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  Rectangle,
} from "recharts";
import { AppProvider } from "../../ContextApi/AppProvider";
import { PedidoService } from "../../Services/PedidoService";
import { FiltroRelatorio } from "../Shared/Interfaces/Pedido";
import { RelatorioPedidosPorCanal } from "../Shared/Interfaces/RelatorioPedidosPorCanal";
import { RelatorioPedidosPorDiaECanal } from "../Shared/Interfaces/RelatorioPedidosPorDiaECanal";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DefaultLoader from "../Shared/DefaultLoader";
import colors from "../../theme/colors";

export default function RelatoriosPedidos() {
  const contextApi = useContext(AppProvider);
  const pedidoService = new PedidoService();
  const [loading, setLoading] = useState(false);
  const [dataInicial, setDataInicial] = useState<Dayjs>(
    dayjs().subtract(7, "day")
  );
  const [dataFinal, setDataFinal] = useState<Dayjs>(dayjs());
  const [relatorioPedidosPorCanal, setRelatorioPedidosPorCanal] = useState<
    RelatorioPedidosPorCanal[]
  >([]);
  const [relatorioPedidosPorDiaECanal, setRelatorioPedidosPorDiaECanal] = useState<
    RelatorioPedidosPorDiaECanal[]
  >([]);

  const PesquisarPor = () => {
    setLoading(true);
    const filtroPesquisa: FiltroRelatorio = {
      dataInicial: dataInicial != null ? dataInicial.toISOString() : null,
      dataFinal: dataFinal != null ? dataFinal.toISOString() : null,
    };

    // Buscar relatórios
    Promise.all([
      pedidoService.gerarRelatorioPedidosPorCanal(filtroPesquisa),
      pedidoService.gerarRelatorioPedidosPorDiaECanal(filtroPesquisa)
    ])
      .then(([responseCanal, responseDiaECanal]) => {
        if (responseCanal.status === 200) {
          const dataCanal = Array.isArray(responseCanal.data) ? responseCanal.data : [];
          setRelatorioPedidosPorCanal(dataCanal);
        } else if (responseCanal.status === 204) {
          setRelatorioPedidosPorCanal([]);
        }
        
        if (responseDiaECanal.status === 200) {
          const dataDiaECanal = Array.isArray(responseDiaECanal.data) ? responseDiaECanal.data : [];
          setRelatorioPedidosPorDiaECanal(dataDiaECanal);
        } else if (responseDiaECanal.status === 204) {
          setRelatorioPedidosPorDiaECanal([]);
        }
        
        // Verificar se ambos retornaram 204
        if (responseCanal.status === 204 && responseDiaECanal.status === 204) {
          contextApi.showMessage("Nenhum dado encontrado para o período selecionado", true, "info");
        }
        
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        // Verificar se é status 204
        if (error.response?.status === 204) {
          setRelatorioPedidosPorCanal([]);
          setRelatorioPedidosPorDiaECanal([]);
          contextApi.showMessage("Nenhum dado encontrado para o período selecionado", true, "info");
        } else {
          const errorMessage = error.response?.data || "Erro ao gerar relatório";
          contextApi.showMessage(errorMessage, true, "error");
        }
      });
  };

  // Função para obter canais únicos dinamicamente
  const obterCanaisUnicos = () => {
    const canais = new Set<string>();
    
    // Adicionar canais do relatório por dia e canal
    relatorioPedidosPorDiaECanal.forEach(item => {
      const canal = item.canalComunicacao || "EM BRANCO";
      canais.add(canal);
    });
    
    // Adicionar canais do relatório por canal (caso não estejam no primeiro)
    relatorioPedidosPorCanal.forEach(item => {
      const canal = item.canalComunicacao || "EM BRANCO";
      canais.add(canal);
    });
    
    return Array.from(canais);
  };

  // Função para processar dados para o gráfico
  const processarDadosParaGrafico = () => {
    if (!relatorioPedidosPorDiaECanal.length) return [];

    const canaisUnicos = obterCanaisUnicos();

    // Agrupar por data
    const dadosAgrupados = relatorioPedidosPorDiaECanal.reduce((acc, item) => {
      const data = new Date(item.dataCriacao).toLocaleDateString('pt-BR');
      const canal = item.canalComunicacao || "EM BRANCO";
      
      if (!acc[data]) {
        // Inicializar com todos os canais únicos
        acc[data] = {
          data: data,
          ...Object.fromEntries(canaisUnicos.map(canal => [canal, 0]))
        };
      }
      
      acc[data][canal] += item.quantidade;
      return acc;
    }, {} as Record<string, any>);

    // Converter para array e ordenar por data
    return Object.values(dadosAgrupados).sort((a, b) => 
      new Date(a.data.split('/').reverse().join('-')).getTime() - 
      new Date(b.data.split('/').reverse().join('-')).getTime()
    );
  };

  // Função para gerar cores dinamicamente
  const gerarCorParaCanal = (canal: string, index: number) => {
    const cores = [
      "#1976D2", // Azul principal
      "#2E7D32", // Verde escuro
      "#FF6F00", // Laranja vibrante
      "#9C27B0", // Roxo (muito diferente do laranja)
      "#D32F2F", // Vermelho
      "#FFC107", // Amarelo
      "#FF5722", // Vermelho laranja
      "#3F51B5", // Índigo
      "#009688", // Teal
      "#795548", // Marrom
      "#607D8B", // Azul acinzentado
      "#E91E63", // Rosa vibrante
    ];
    
    return cores[index % cores.length];
  };

  // Componente de tooltip personalizado
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, boxShadow: 3, bgcolor: 'white' }}>
          <Typography variant="h6" sx={{ color: colors.bluePrimary, mb: 1 }}>
            📅 {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} sx={{ mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: entry.color }}>
                {entry.name}: {entry.value} pedidos
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }
    return null;
  };

  React.useEffect(() => {
    PesquisarPor();
  }, []);

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Linha 1 - Resumo dos Relatórios (espaço 12) 
          <Grid item xs={12}>
            <Paper sx={{ p: 1, bgcolor: '#f8f9fa' }}>
              <h4 style={{ margin: '0 0 0 0', color: colors.bluePrimary, textAlign: 'left' }}>
                📊 Relatórios de Pedidos
              </h4>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                Análise detalhada dos pedidos por diferentes critérios
              </Typography>
            </Paper>
          </Grid>
*/}
          {/* Linha 2 - Filtros de Data (espaço 12) */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>🗓️ Filtros de Período</h4>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <DemoContainer components={["DatePicker"]}>
                      <DatePicker
                        label="Data Inicial"
                        defaultValue={dataInicial}
                        value={dataInicial}
                        format="DD/MM/YYYY"
                        onChange={(newValue) =>
                          setDataInicial(newValue ?? dataInicial)
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                          },
                        }}
                      />
                    </DemoContainer>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DemoContainer components={["DatePicker"]}>
                      <DatePicker
                        value={dataFinal}
                        onChange={(newValue) =>
                          setDataFinal(newValue ?? dataFinal)
                        }
                        label="Data Final"
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                          },
                        }}
                      />
                    </DemoContainer>
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.5}>
                  <Button
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{ height: '40px', width: "100%" }}
                    onClick={() => PesquisarPor()}
                  >
                    {loading ? (
                      <DefaultLoader
                        height="2.5em"
                        width="2.5em"
                        colors={[
                          "#1876D2",
                          "#1876D2",
                          "#1876D2",
                          "#1876D2",
                          "#1876D2",
                        ]}
                        textInfo="Gerando Relatório..."
                      />
                    ) : (
                      "Pesquisar"
                    )}
                  </Button>
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </Paper>
          </Grid>

          {/* Linha 3 - Cards de Resumo por Canal (espaço 12) */}
          {relatorioPedidosPorCanal.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 1, mb: 1 }}>
                <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>📱 Resumo por Canal de Comunicação</h4>
                
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={1}>
                    {relatorioPedidosPorCanal.map((item, index) => (
                      <Grid item xs={6} sm={4} md={2} key={index}>
                        <Box 
                          sx={{ 
                            p: 1, 
                            bgcolor: gerarCorParaCanal(item.canalComunicacao || "EM BRANCO", index),
                            borderRadius: 2, 
                            color: "white",
                            boxShadow: 2,
                            textAlign: 'center',
                            height: '90%'
                          }}
                        >
                          <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                            {item.quantidade}
                          </div>
                          <div style={{ fontSize: '12px' }}>{item.canalComunicacao || "EM BRANCO"}</div>
                        </Box>
                      </Grid>
                    ))}
                    
                    {/* Card do total geral */}
                    <Grid item xs={6} sm={4} md={2}>
                      <Box sx={{ 
                        p: 1, 
                        bgcolor: "success.main", 
                        borderRadius: 2, 
                        color: "white",
                        boxShadow: 2,
                        textAlign: 'center',
                        height: '90%'
                      }}>
                        <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                          {relatorioPedidosPorCanal.reduce((total, item) => total + item.quantidade, 0)}
                        </div>
                        <div style={{ fontSize: '12px' }}>Total Geral</div>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Linha 4 - Análise Detalhada com Gráfico (espaço 12) */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>📊 Análise Detalhada por Dia e Canal</h4>
              
              <Paper sx={{ p: 2, height: "100%", boxShadow: 1, border: '1px solid #e0e0e0' }}>
                {/* <Typography variant="h6" sx={{ color: colors.bluePrimary, fontWeight: "bold", mb: 2 }}>
                  📈 Evolução Temporal dos Pedidos por Canal
                </Typography>*/}
                
                {relatorioPedidosPorDiaECanal.length > 0 ? (
                  <Box sx={{ height: '500px', width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={processarDadosParaGrafico()}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        barCategoryGap="10%"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="data" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          fontSize={12}
                        />
                        <YAxis 
                          label={{ 
                            value: 'Quantidade de Pedidos', 
                            angle: -90, 
                            position: 'insideLeft',
                            fontSize: 12
                          }}
                        />
                        <Tooltip content={CustomTooltip} />
                        <Legend 
                          verticalAlign="top" 
                          height={36}
                          wrapperStyle={{ paddingBottom: '10px' }}
                        />
                        
                        {/* Barras para cada canal de comunicação - Geradas dinamicamente */}
                        {obterCanaisUnicos().map((canal, index) => (
                          <Bar
                            key={canal}
                            dataKey={canal}
                            name={canal}
                            stackId="a"
                            fill={gerarCorParaCanal(canal, index)}
                            activeBar={<Rectangle fill={gerarCorParaCanal(canal, index)} stroke="#1C1C1C" />}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                    
                    {/* Resumo dos dados abaixo do gráfico */}
                    <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ color: '#666', textAlign: 'center' }}>
                        📊 Total de {relatorioPedidosPorDiaECanal.length} registros no período selecionado
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Nenhum dado disponível para o período selecionado
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
